import { IUserIdentity } from "@/types/auth.types";
import { IParticipant } from "@/types/participants.types";
import { Dictionary } from "@/types/utils.types";

export const isMobileTabletDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile =
    /iphone|ipod|android.*mobile|blackberry|mini|wap|windows\sce|palm/i.test(
      userAgent,
    );
  const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(
    userAgent,
  );
  const isIPad = /ipad/i.test(userAgent);
  const isIPhone = /iphone/i.test(userAgent);

  return isMobile || isTablet || isIPhone || isIPad;
};

/**
 * Joins an array of strings (or other stringifiable items) into a phrase, using commas and "and" for a natural-sounding result.
 *
 * @template T The type of items in the array (must be convertible to a string).
 * @param {T[]} items The array of items to join.
 * @returns {string} A phrase representing the items, or an empty string if the array is empty.
 *          Examples:
 *          - `["apple", "banana", "cherry"]` becomes `"apple, banana and cherry"`
 *          - `["apple", "banana"]` becomes `"apple and banana"`
 *          - `["apple"]` becomes `"apple"`
 *          - `[]` becomes `""`
 */
export const joinArrayIntoPhrase = <T>(items: T[]): string => {
  if (!items || items.length === 0) {
    return "";
  }

  if (items.length === 1) {
    return String(items[0]);
  }

  const allButLast = items.slice(0, -1).join(", "); // Everything up to the last element
  const last = String(items[items.length - 1]); // The last element
  return `${allButLast} and ${last}`;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
export const loadScriptonDemand = (
  scriptUrl: string | undefined,
  onLoad: EventListener,
) => {
  if (scriptUrl) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = scriptUrl;
    const headScript = document.getElementsByTagName("script")[0];
    if (headScript.parentNode) {
      headScript.parentNode.insertBefore(script, headScript);
    }
    script.addEventListener("load", onLoad);
  }
};

export const getServerTime = (offsetTime: number | undefined): Date => {
  const newOffsetTime = offsetTime ?? 0;
  const newDate = new Date();
  if (newOffsetTime) {
    newDate.setTime(newDate.getTime() + newOffsetTime);
  }
  return newDate;
};

export const setAudioInLocalStorage = (
  slug: string | undefined,
  audio: boolean,
) => {
  localStorage.setItem(`${slug}-audio`, audio ? "true" : "false");
};

export const setVideoInLocalStorage = (
  slug: string | undefined,
  video: boolean,
) => {
  localStorage.setItem(`${slug}-video`, video ? "true" : "false");
};

export const getAudio = (slug: string | undefined) => {
  const audio: string | null = localStorage.getItem(`${slug}-audio`);
  // eslint-disable-next-line no-nested-ternary
  return audio === "true" ? true : audio === "false" ? false : null;
};

export const getVideo = (slug: string | undefined) => {
  const video = localStorage.getItem(`${slug}-video`);
  // eslint-disable-next-line no-nested-ternary
  return video === "true" ? true : video === "false" ? false : null;
};

export const getParticipantNamesWithoutMe = (
  participants: Dictionary<IParticipant>,
  userIdentityData: IUserIdentity | null,
) =>
  Object.values(participants)
    .filter(
      (participant) =>
        participant.email !== userIdentityData?.email &&
        participant.inInterview,
    )
    .map((participant) => participant.name);

export function template(
  strings: TemplateStringsArray,
  ...keys: (string | number)[]
) {
  return (...values: any[]) => {
    const dict = values[values.length - 1] || {};
    const result: (string | number)[] = [strings[0]];
    keys.forEach((key, i) => {
      const value = Number.isInteger(key)
        ? values[key as number]
        : dict[key as string];
      result.push(value, strings[i + 1]);
    });
    return result.join("");
  };
}

export const generateUniqueId = (serverTimeOffsetTime: number) => {
  const timestamp = getServerTime(serverTimeOffsetTime).getTime();
  const random = Math.floor(Math.random() * 1000000);
  const domain = window.location.hostname;
  const uniqueId = `${timestamp}_${random}_${domain}`;
  return uniqueId;
};

/**
 * Converts seconds to milliseconds.
 *
 * @param {number} second - The number of seconds to convert.
 * @returns {number} - The equivalent time in milliseconds.
 */
export const convertSecToMs = (second: number): number => second * 1000;
